import { ClientBaseQuote } from '../../../../classes/quote/ClientBaseQuote';
import { SelectBasicComponent } from '../../form/base-components/select-basic/select-basic.component';
import { BasicCheckboxComponent } from '../../form/base-components/basic-checkbox/basic-checkbox.component';
import { ProductQuoteResponse, ProductQuoteResponseList } from '../../../../js/interfaces/CoverpathTypes';
import { QuoteService } from '../../../services/quote/quote.service';
import { Directive, OnInit, ViewChild } from '@angular/core';
import { BaseQuickQuoteService } from '../../../services/quote/base-quick-quote.service';
import { PremiumRequestList } from '../../../../../common/js/productService/ProductServiceClient';
import { BasicTextInputComponent } from '../../form/base-components/base-text-input/basic-text-input.component';
import { RadioGroupComponent } from '../../form/base-components/radio-group/radio-group.component';
import { ValidationService } from '../../../services/validation/validation.service';
import { AmorphicService } from '../../../services/amorphic/amorphic.service';
import { ApplicationService } from '../../../services/application/application.service';
import { Router } from '@angular/router';
import { Controller } from '../../../../../coverpath-client/public/js/Controller';
import { FrequencyUnit, Product, RiderConstants } from '../../../../../common/js/static/Product';
import { CoverpathTermQuote } from '../../../../classes/quote/coverpath/CoverpathTermQuote';
import { BaseQuote } from '../../../../../common/js/quote/BaseQuote';
import { CommonUtils } from '../../../../../common/js/static/CommonUtils';

const healthClassTooltips = [
  "Excellent: Your cholesterol and blood pressure are normal, you aren't overweight, and don't have any medical conditions.",
  "Very good: You generally eat right, aren't overweight and have good blood pressure and cholesterol levels, either with or without medication.",
  'Good: You may have somewhat high blood pressure or cholesterol levels and may be overweight. You may have some history of medical conditions.',
  'Average: Your blood pressure or cholesterol levels are high, you are overweight or you have a history of medical conditions.',
];

@Directive()
export abstract class BaseQuickQuoteComponent implements OnInit {
  private quoteService: QuoteService;
  private quickQuoteService: BaseQuickQuoteService;
  private validationService: ValidationService;
  private applicationService: ApplicationService;
  private amorphicService: AmorphicService;
  private router: Router;

  healthClassText: string;
  estimatedMonthlyPremium: string;
  estimatedAnnualPremium: string;
  estimatedWOP: string;
  showWOP: boolean = true;
  showECP: boolean = false;
  frequencyUnit: string;
  applyEnabled: boolean = false;
  controller: Controller;
  paymentFrequency: string;
  isAuthenticationPathResolved: boolean = false;

  @ViewChild('genderInput') genderInput: SelectBasicComponent;
  @ViewChild('stateInput') stateInput: SelectBasicComponent;
  @ViewChild('healthNumberInput') healthNumberInput: SelectBasicComponent;
  @ViewChild('smokerInput') smokerInput: SelectBasicComponent;
  @ViewChild('termInput', { static: true }) termInput: SelectBasicComponent;
  @ViewChild('dateOfBirthInput', { static: true }) dateOfBirthInput: BasicTextInputComponent;
  @ViewChild('faceAmountInput', { static: true }) faceAmountInput: BasicTextInputComponent;
  @ViewChild('paymentFrequencyInput') term: RadioGroupComponent;
  @ViewChild('waiverOfPremiumInput') waiverOfPremiumInput: BasicCheckboxComponent;
  @ViewChild('extendedConvertibilityInput') extendedConvertibilityInput: BasicCheckboxComponent;

  constructor(
    quoteService: QuoteService,
    quickQuoteService: BaseQuickQuoteService,
    validationService: ValidationService,
    applicationService: ApplicationService,
    amorphicService: AmorphicService,
    router: Router
  ) {
    this.quoteService = quoteService;
    this.quickQuoteService = quickQuoteService;
    this.validationService = validationService;
    this.applicationService = applicationService;
    this.amorphicService = amorphicService;
    this.router = router;
    this.controller = this.amorphicService.controller as Controller;
  }

  async ngOnInit(): Promise<void> {
    this.loadQuickQuote();
    this.quickQuoteService.triggerQuickQuoteLoad.subscribe(() => {
      this.loadQuickQuote();
    });

    await this.resolveAuthenticationPath();
  }

  protected loadQuickQuote(): void {
    const isUpdate = false;
    const quote = this.quickQuoteService.getCurrentQuote();
    const clientQuote = quote as ClientBaseQuote;
    this.initializeProperties();
    this.validateEligibilityForWaiverOfPremium();
    if (quote.productType === Product.productTypes.Term.code) {
      this.validateEligibilityForExtendedConvertibility();
    }
    this.setQuoteProperties(clientQuote);
    this.attemptQuoteFetch();
    if (isUpdate) {
      this.updateAllViewChildren();
    }
  }

  protected updateAllViewChildren(): void {
    this.genderInput.updateFromModel();
    this.stateInput.updateFromModel();
    this.healthNumberInput.updateFromModel();
    this.smokerInput.updateFromModel();
    this.termInput.updateFromModel();
    this.dateOfBirthInput.updateFromModel();
    this.faceAmountInput.updateFromModel();
    if (this.waiverOfPremiumInput) {
      // will not be present if age > 59
      this.waiverOfPremiumInput.updateFromModel();
    }
    if (this.extendedConvertibilityInput) {
      // will not be present for 10 year term
      this.extendedConvertibilityInput.updateFromModel();
    }
  }

  abstract initializeProperties(): void;

  protected setQuoteProperties(quote: ClientBaseQuote): void {
    this.paymentFrequency = quote.paymentFrequency;
    this.healthClassText = healthClassTooltips[quote.healthClassNumber];
    this.frequencyUnit = FrequencyUnit[quote.paymentFrequency];
  }

  abstract calculateHealthClass(): void;

  public handleChangeAndAttemptQuoteFetch(source?: string): void {
    this.handleChange(source);
    this.attemptQuoteFetch();
  }

  private handleChange(source?: string): void {
    this.calculateHealthClass();

    this.validateEligibilityForWaiverOfPremium();

    const quote = this.quickQuoteService.getCurrentQuote() as ClientBaseQuote;
    if (quote.productType === Product.productTypes.Term.code) {
      this.validateEligibilityForExtendedConvertibility();
    }

    const isMMDirectProduct =
      quote.product.match(Product.products.MMDirectTerm.code) ||
      quote.product.match(Product.products.MMDirectTermECP.code);
    if (isMMDirectProduct || !this.controller.nonroundedFaceAmountsEnabled) {
      quote.faceAmount = Math.round(quote.faceAmount / 1000) * 1000; // Round to nearest thousand
    }

    this.faceAmountInput.updateFromModel();

    //Don't re-run validation if the face amount field was edited directly (it was done in the form service)
    if (source !== 'faceAmountInput') {
      this.faceAmountInput.triggerBase();
    }

    const selectedTermOption = this.termInput.options.find((option) => option.value === this.termInput.selectedItem);
    this.termInput.triggerBase(selectedTermOption);
  }

  protected validateEligibilityForWaiverOfPremium(): void {
    const quote = this.quickQuoteService.getCurrentQuote();
    // STOP if dob is not provided or invalid
    if (!quote.dateOfBirth) {
      return;
    }

    this.showWOP = this.isQuoteValidForRider(quote, 'WOP');
    if (!this.showWOP) {
      quote.waiverOfPremium = false;
    }
  }

  protected validateEligibilityForExtendedConvertibility(): void {
    const quote = this.quickQuoteService.getCurrentQuote() as CoverpathTermQuote;
    // STOP if dob is not provided or invalid
    if (!quote.dateOfBirth) {
      return;
    }

    this.showECP = this.isQuoteValidForRider(quote, 'ECP');
    if (!this.showECP) {
      quote.hasEcp = false;
    }
  }

  protected isQuoteValidForRider(quote: BaseQuote, rider: 'ECP' | 'WOP'): boolean {
    const dateOfBirth = new Date(quote.dateOfBirth);

    if (rider === 'ECP') {
      const term = quote.term;
      const isMMDirectProduct = quote.product.match(Product.products.MMDirectTerm.code);
      const isTobaccoUser = quote.smoker;
      const productSpecs = isMMDirectProduct
        ? Product.products.MMDirectTermECP.productSpecs
        : Product.products.CASTermECP.productSpecs;
      return (
        term >= productSpecs.minTerm &&
        !ValidationService.isOutsideAgeLimit(dateOfBirth, isTobaccoUser, productSpecs, term.toString())
      );
    } else if (rider === 'WOP') {
      const productMaxAge = quote.smoker
        ? Product.products[quote.product].productSpecs.termBreakdown[quote.term].tobaccoMaxAge ||
          Product.products[quote.product].productSpecs.termBreakdown[quote.term].maxAge
        : Product.products[quote.product].productSpecs.termBreakdown[quote.term].maxAge;
      const maxAge = Math.min(RiderConstants.wop.maxAge, productMaxAge);
      return (
        CommonUtils.isOlderThanOrEqualToAge(dateOfBirth, RiderConstants.wop.minAge) &&
        CommonUtils.isYoungerThanAge(dateOfBirth, maxAge)
      );
    }
  }

  protected async attemptQuoteFetch(): Promise<void> {
    if (!this.controller.isApplicationSubmitted()) {
      this.updateApplicationValues();
    }

    if (this.validateQuote()) {
      await this.fetchAndUpdateProductQuote();
      this.applyEnabled = true;
    } else {
      this.applyEnabled = false;
    }
  }

  private updateApplicationValues(): void {
    const quote = this.quickQuoteService.getCurrentQuote();

    this.applicationService.updateApplicationValues(quote);
  }

  private validateQuote(): boolean {
    const quote = this.quickQuoteService.getCurrentQuote();
    return (
      quote.gender &&
      quote.healthClass &&
      quote.dateOfBirth &&
      quote.contractState &&
      quote.term &&
      quote.faceAmount &&
      typeof quote.waiverOfPremium !== 'undefined' &&
      !this.dateOfBirthInput.isError &&
      !this.faceAmountInput.isError &&
      !this.termInput.isError
    );
  }

  applyNow(): void {
    this.quickQuoteService.setCustomerQuote();
    this.quickQuoteService.routeCustomer();
  }

  public async fetchAndUpdateProductQuote(): Promise<void> {
    const quote = this.quickQuoteService.getCurrentQuote();

    // 'additionalRequests' array will be loaded with the string keywords needed to create additional requests
    const additionalRequests: Array<string> = ['oppositePaymentFrequency'];

    const quoteRequestList: PremiumRequestList = QuoteService.convertQuoteToProductServiceRequestList(
      quote,
      additionalRequests
    );

    if (this.showWOP) {
      // Force fetch of waiver, for display purposes, whether or not it is selected.
      quoteRequestList.requests[0].waiver = true;
    }

    /**
     * quoteRequestList:
     *
     * first request is for selected on-screen values (Monthly/Yearly, ECP/Non ECP)
     *
     * second request is for the opposite payment frequency than what was selected (so if "Monthly" is selected, this request uses "Annual" and vice versa)
     *
     * third request (exists if quote.term is not 10 because ECP not supported when term length is 10) is for the "opposite" ECP value
     * if the 'Purchase Extended Period of Convertibility' checkbox is checked, than the first request will use ECP product and the third request will not
     * if it is not checked, than the first request will not use ECP product and the third request will
     */
    try {
      const quoteResponseList: ProductQuoteResponseList = await this.quoteService.fetchMultipleProductQuotes(
        quoteRequestList
      );

      // first response - selected on-screen values (Monthly/Yearly, ECP/Non-ECP)
      const quoteResponse: ProductQuoteResponse = quoteResponseList.responses[0];
      const quoteResponseBaseValue = quoteResponse.base.valueOf();
      const quoteWaiverOfPremiumValue = quoteResponse.waiver.valueOf();

      quote.premium = quoteResponseBaseValue;
      quote.waiverOfPremiumAmount = quoteWaiverOfPremiumValue;

      this.estimatedWOP = quoteWaiverOfPremiumValue ? quoteWaiverOfPremiumValue.toFixed(2) : '';

      // second response - opposite payment frequency than what was selected
      const oppPaymentFreqQuoteResponse: ProductQuoteResponse = quoteResponseList.responses[1];

      if (CommonUtils.makePaymentFrequencySafeForCoverpathQuote(quote.paymentFrequency) === 'Annual') {
        this.estimatedAnnualPremium = quote.waiverOfPremium
          ? (quoteResponseBaseValue + quoteWaiverOfPremiumValue).toFixed(2)
          : quoteResponseBaseValue.toFixed(2);
        this.estimatedMonthlyPremium = quote.waiverOfPremium
          ? (oppPaymentFreqQuoteResponse.base + oppPaymentFreqQuoteResponse.waiver).toFixed(2)
          : oppPaymentFreqQuoteResponse.base.toFixed(2);
      } else {
        this.estimatedAnnualPremium = quote.waiverOfPremium
          ? (oppPaymentFreqQuoteResponse.base + oppPaymentFreqQuoteResponse.waiver).toFixed(2)
          : oppPaymentFreqQuoteResponse.base.toFixed(2);
        this.estimatedMonthlyPremium = quote.waiverOfPremium
          ? (quoteResponseBaseValue + quoteWaiverOfPremiumValue).toFixed(2)
          : quoteResponseBaseValue.toFixed(2);
      }
    } catch (error) {
      this.amorphicService.amorphicLogError(
        'BaseQuickQuoteComponent',
        'fetchAndUpdateProductQuote',
        "there was a problem fetching and updating the quote's premium and wop",
        error
      );
    }
  }

  private async resolveAuthenticationPath(): Promise<void> {
    if (this.controller) {
      await this.controller.resolveAuth0Data();
      this.isAuthenticationPathResolved = true;
    }
  }
}
